/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useMemo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';

import AvatarGroup from '@atlaskit/avatar-group';
import { cssMap, jsx } from '@atlaskit/css';
import { type RecaptchaResult } from '@atlassian/google-recaptcha';
import Heading from '@atlaskit/heading';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAnalytics } from '../../analytics';
import { InstrumentedCustomThemeButton as InstrumentedButton } from '../../analytics/InstrumentedButton';
import { AnalyticsActionSubjectId } from '../../types';
import { messages } from '../i18n';
import { type OptionType } from '../InviteeList/InviteeListContext';
import ProductCheckbox from '../ProductCheckbox/ProductCheckbox';
import { InviteSuccessContentAnalytics } from './InviteSuccessContentAnalytics';
import { useProducts } from '../ProductSelect';
import { Success } from './images/success';
import useInvisibleRecaptchaWithUFO from './useInvisibleRecaptchaWithUfo';

export interface InviteSuccessContentSuccessText {
	title: React.ReactNode;
	description: React.ReactNode;
}
export interface InviteSuccessContentProps {
	availableProducts: string[];
	invitees: OptionType[];
	invitedProducts: string[];
	successfulInviteText: InviteSuccessContentSuccessText;
	isSendingInvitation: boolean;
	onCancelButtonClick?: () => void;
	onInviteButtonClick: (
		products: string[],
		isSuccessModal: true,
		getRecaptchaToken: RecaptchaResult['getRecaptchaToken'],
	) => Promise<void> | void;
}

const styles = cssMap({
	wrapper: {
		paddingBlock: token('space.300', '24px'),
		paddingInline: token('space.300', '24px'),
	},
	productCheckboxWrapper: {
		paddingBlockStart: token('space.250', '20px'),
		paddingBlockEnd: token('space.300', '24px'),
		borderTopColor: token('color.border', '#091E4224'),
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width', '1px'),
		marginTop: token('space.300', '24px'),
	},
	inviteSuccessDescription: {
		paddingTop: token('space.200', '16px'),
	},
});

export const InviteSuccessContent = ({
	availableProducts,
	invitees,
	invitedProducts,
	successfulInviteText,
	isSendingInvitation,
	onCancelButtonClick,
	onInviteButtonClick,
}: InviteSuccessContentProps) => {
	const analytics = useAnalytics(InviteSuccessContentAnalytics);
	const jiraProduct = availableProducts.find((product) => product === 'jira-software');
	const confluenceProduct = availableProducts.find((product) => product === 'confluence');
	const jpdProduct = availableProducts.find((product) => product === 'jira-product-discovery');
	const invitedProduct: string | undefined =
		invitedProducts.find((productId) => productId === 'jira-software') ??
		invitedProducts.find((productId) => productId === 'confluence') ??
		invitedProducts.find((productId) => productId === 'jira-product-discovery') ??
		invitedProducts[0];

	const { selectedProducts, setSelectedProducts } = useProducts();
	const { getRecaptchaToken, isRecaptchaLoading } = useInvisibleRecaptchaWithUFO();

	const attributes = useMemo(
		() => ({
			products: availableProducts,
			invitees: invitees.length,
		}),
		[availableProducts, invitees.length],
	);

	useEffect(() => {
		analytics.driveMultiProductsSuccessModalViewed(attributes);
	}, [analytics, attributes]);

	const handleAddButtonClick = async () => {
		await onInviteButtonClick(selectedProducts, true, getRecaptchaToken);
	};

	const handleProductCheckboxChange = (isChecked: boolean, productId: string) => {
		if (isChecked) {
			setSelectedProducts([...selectedProducts, productId]);
			analytics.productCheckboxChecked(productId);
		} else {
			setSelectedProducts(selectedProducts.filter((product) => product !== productId));
			analytics.productCheckboxUnchecked(productId);
		}
	};

	return (
		<Box testId="testId-invite-people-invite-success-content" xcss={styles.wrapper}>
			<Inline alignBlock="center" space="space.100">
				<Success />
				<Heading size="medium">{successfulInviteText.title}</Heading>
			</Inline>
			<Stack xcss={styles.inviteSuccessDescription}>
				<Text>{successfulInviteText.description}</Text>
			</Stack>
			<Box xcss={styles.productCheckboxWrapper}>
				<Inline alignBlock="center" space="space.050">
					<AvatarGroup
						data={invitees.map((member, index) => ({
							key: member.id,
							src: member.avatarUrl,
							name: member.name,
						}))}
						maxCount={3}
					/>
					<Text>
						<FormattedMessage
							{...messages.multiProductInviteAddProductHelper}
							values={{
								user: invitees[0].name,
								count: invitees.length,
								b: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
							}}
						/>
					</Text>
				</Inline>
				{jiraProduct && (
					<ProductCheckbox
						pairedProductId={invitedProduct!}
						productId={jiraProduct}
						onChange={handleProductCheckboxChange}
					/>
				)}
				{confluenceProduct && (
					<ProductCheckbox
						pairedProductId={invitedProduct!}
						productId={confluenceProduct}
						onChange={handleProductCheckboxChange}
					/>
				)}
				{jpdProduct && (
					<ProductCheckbox
						pairedProductId={invitedProduct!}
						productId={jpdProduct}
						onChange={handleProductCheckboxChange}
					/>
				)}
			</Box>
			<Flex justifyContent="end" gap="space.100">
				<InstrumentedButton
					actionSubjectId={AnalyticsActionSubjectId.EXIT_DRIVE_MULTI_PRODUCTS_SUCCESS_MODAL}
					appearance="subtle"
					isDisabled={isSendingInvitation}
					onClick={onCancelButtonClick}
					analyticsAttributes={attributes}
				>
					<FormattedMessage {...messages.multiProductInviteCancelButton} />
				</InstrumentedButton>
				<InstrumentedButton
					actionSubjectId={AnalyticsActionSubjectId.SUBMIT_DRIVE_MULTI_PRODUCTS_SUCCESS_MODAL}
					appearance="primary"
					type="submit"
					isLoading={isSendingInvitation || isRecaptchaLoading}
					isDisabled={selectedProducts.length === 0}
					onClick={handleAddButtonClick}
					analyticsAttributes={{ ...attributes, selectedProducts }}
				>
					<FormattedMessage {...messages.multiProductInviteSubmitButton} />
				</InstrumentedButton>
			</Flex>
		</Box>
	);
};

export default InviteSuccessContent;
